<template>
    <modal-shell
        ref="shell"
        :show="show"
        title-id="decision-modal-title"
        description-id="decision-modal-description"
        modal-class="max-w-md"
        :initial-focus-selector="mode === 'inform' ? '[data-modal-primary]' : '[data-modal-cancel]'"
        @escape="handleCancel"
    >
        <div class="foxy-modal-section p-6 border-b">
            <div class="flex items-center space-x-3">
                <div v-if="type === 'warning'"
                     class="flex-shrink-0 w-10 h-10 rounded-full bg-foxy-warning/10 flex items-center justify-center">
                    <svg class="w-6 h-6 text-foxy-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <div v-else
                     class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 id="decision-modal-title" class="text-xl font-bold text-foxy-text flex-1">
                    {{ title }}
                </h3>
            </div>
        </div>

        <div class="p-6">
            <p id="decision-modal-description" class="text-foxy-text-secondary mb-4 whitespace-pre-wrap">{{ message }}</p>

            <div v-if="mode === 'prompt'" class="mb-4">
                <input
                    ref="promptInput"
                    v-model="promptValue"
                    type="text"
                    class="foxy-input !py-2"
                    :placeholder="promptPlaceholder"
                    @keyup.enter="handleConfirm"
                    @keyup.esc="handleCancel"
                >
            </div>

            <fieldset v-if="mode === 'selection'" class="space-y-2">
                <legend class="sr-only">{{ title }}</legend>
                <label v-for="choice in choices"
                       :key="choice.value"
                       class="flex min-h-11 cursor-pointer items-start gap-3 rounded-xl border bg-white p-3 transition-colors dark:bg-gray-900"
                       :class="Number(selectionValue) === Number(choice.value)
                           ? 'border-indigo-500 ring-2 ring-indigo-200 dark:border-indigo-400 dark:ring-indigo-900/60'
                           : 'border-gray-200 hover:border-indigo-300 dark:border-gray-600 dark:hover:border-indigo-500'">
                    <input v-model="selectionValue"
                           type="radio"
                           :value="choice.value"
                           class="mt-1"
                           :aria-label="choice.label">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ choice.label }}</span>
                </label>
            </fieldset>
        </div>

        <div class="foxy-modal-section p-6 border-t"
             :class="mode === 'choice' ? 'flex flex-col gap-3' : 'flex justify-end space-x-3'">
            <template v-if="mode === 'choice'">
                <button v-for="choice in choices"
                        :key="choice.value"
                        type="button"
                        class="min-h-11 w-full !py-2"
                        :class="choice.variant === 'danger' ? 'foxy-btn-destructive' : 'foxy-btn-primary'"
                        @click="handleChoice(choice.value)">
                    {{ choice.label }}
                </button>
                <button type="button"
                        data-modal-cancel
                        class="foxy-btn-secondary min-h-11 w-full !py-2"
                        @click="handleCancel">
                    {{ choiceCancelText }}
                </button>
            </template>
            <template v-else>
                <button v-if="mode !== 'inform'"
                        type="button"
                        data-modal-cancel
                        class="foxy-btn-secondary min-h-11 !py-2"
                        @click="handleCancel">
                    {{ cancelText }}
                </button>
                <button type="button"
                        data-modal-primary
                        class="foxy-btn-primary min-h-11 !py-2 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="mode === 'selection' && selectionValue === null"
                        @click="handleConfirm">
                    {{ confirmText }}
                </button>
            </template>
        </div>
    </modal-shell>
</template>

<script>
import ModalShell from './ModalShell.vue';

export default {
    name: 'DecisionModal',
    components: {
        ModalShell,
    },
    data() {
        return {
            show: false,
            mode: 'confirm',
            type: 'warning',
            title: '',
            message: '',
            confirmText: 'Підтвердити',
            cancelText: 'Скасувати',
            promptValue: '',
            promptPlaceholder: '',
            choices: [],
            choiceCancelText: 'Скасувати',
            selectionValue: null,
            resolve: null,
            reject: null,
            sessionGeneration: 0,
            activeSessionId: null,
        };
    },
    beforeDestroy() {
        this.activeSessionId = null;
        this.sessionGeneration += 1;
    },
    methods: {
        inform(message, title = 'Повідомлення', type = 'info') {
            return new Promise((resolve) => {
                this.mode = 'inform';
                this.type = type;
                this.title = title;
                this.message = message;
                this.confirmText = 'OK';
                const sessionId = this.openModal();
                this.resolve = () => this.finishSession(sessionId, () => resolve(true));
                this.reject = this.resolve;
            });
        },
        confirm(message, title = 'Підтвердження', type = 'warning') {
            return new Promise((resolve, reject) => {
                this.mode = 'confirm';
                this.type = type;
                this.title = title;
                this.message = message;
                this.confirmText = 'Підтвердити';
                this.cancelText = 'Скасувати';
                const sessionId = this.openModal();
                this.resolve = () => this.finishSession(sessionId, () => resolve(true));
                this.reject = () => this.finishSession(sessionId, () => reject(false));
            });
        },
        choose(message, choices, title = 'Оберіть дію', type = 'warning', cancelText = 'Скасувати') {
            return new Promise((resolve, reject) => {
                this.mode = 'choice';
                this.type = type;
                this.title = title;
                this.message = message;
                this.choices = choices;
                this.choiceCancelText = cancelText;
                const sessionId = this.openModal();
                this.resolve = (value) => this.finishSession(sessionId, () => {
                    this.choices = [];
                    resolve(value);
                });
                this.reject = () => this.finishSession(sessionId, () => {
                    this.choices = [];
                    reject(null);
                });
            });
        },
        selectOne(
            message,
            choices,
            title = 'Оберіть варіант',
            type = 'warning',
            cancelText = 'Скасувати',
            confirmText = 'Продовжити',
            preselectedValue = null,
        ) {
            return new Promise((resolve, reject) => {
                this.mode = 'selection';
                this.type = type;
                this.title = title;
                this.message = message;
                this.choices = choices;
                this.cancelText = cancelText;
                this.confirmText = confirmText;
                this.selectionValue = choices.some(
                    (choice) => Number(choice.value) === Number(preselectedValue),
                ) ? preselectedValue : null;
                const sessionId = this.openModal();
                this.resolve = () => {
                    if (this.selectionValue === null) return;
                    const value = this.selectionValue;
                    this.finishSession(sessionId, () => {
                        this.choices = [];
                        this.selectionValue = null;
                        resolve(value);
                    });
                };
                this.reject = () => this.finishSession(sessionId, () => {
                    this.choices = [];
                    this.selectionValue = null;
                    reject(null);
                });
            });
        },
        prompt(message, defaultValue = '', title = 'Введення', placeholder = '') {
            return new Promise((resolve, reject) => {
                this.mode = 'prompt';
                this.type = 'info';
                this.title = title;
                this.message = message;
                this.promptValue = defaultValue;
                this.promptPlaceholder = placeholder;
                this.confirmText = 'OK';
                this.cancelText = 'Скасувати';
                const sessionId = this.openModal();
                this.resolve = () => {
                    const value = this.promptValue;
                    this.finishSession(sessionId, () => {
                        this.promptValue = '';
                        resolve(value);
                    });
                };
                this.reject = () => this.finishSession(sessionId, () => {
                    this.promptValue = '';
                    reject(null);
                });
            });
        },
        isActive() {
            return this.show && this.activeSessionId !== null;
        },
        handleConfirm() {
            this.resolve?.();
        },
        handleChoice(value) {
            this.resolve?.(value);
        },
        handleCancel() {
            this.reject?.();
        },
        setReturnFocusTarget(element) {
            this.$refs.shell?.setReturnFocusTarget(element);
        },
        openModal() {
            const sessionId = ++this.sessionGeneration;
            this.activeSessionId = sessionId;
            this.show = true;
            return sessionId;
        },
        finishSession(sessionId, settle) {
            if (sessionId !== this.activeSessionId) return;
            this.activeSessionId = null;
            this.show = false;
            this.resolve = null;
            this.reject = null;
            settle();
        },
    },
};
</script>
